import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { openaiUsage } from "../openai/usage";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const DEFAULT_GROQ_BASE_URL = "https://api.groq.com/openai/v1";

const groqModelsFetch = openAiModelsFetch(
  "Fetch models from Groq",
  ({ baseUrl }) => baseUrlEndpoint(baseUrl, DEFAULT_GROQ_BASE_URL, "/models")
);

export const groq = defineConnector({
  identifier: "groq",
  name: "Groq",
  tagLine: "Fast AI inference",
  logo,
  protocol: "http",
  tags: ["ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_GROQ_BASE_URL),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Create an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Go to the [Groq console](https://console.groq.com/keys) and click **Create API Key**. Copy it now — Groq only shows it once.",
        },
      ],
    },
    {
      title: "Paste your API key",
      items: [
        {
          kind: "input",
          label: "API key",
          placeholder: "gsk_...",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "url",
          label: "Base URL (optional)",
          placeholder: DEFAULT_GROQ_BASE_URL,
          defaultValue: DEFAULT_GROQ_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default Groq API endpoint.",
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "openai/gpt-oss-120b" }],
          addLabel: "Add model",
          fetch: groqModelsFetch,
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_GROQ_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    GROQ_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_GROQ_BASE_URL),
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.GROQ_BASE_URL as the Groq API base URL. Pass a placeholder API key; the proxy injects the real key from the connected app.",
      configuration
    ),
  usage: openaiUsage(),
});
